import classNames from "classnames";
import clamp from "lodash/clamp";
import round from "lodash/round";
import React, { CSSProperties, FunctionComponent, useCallback, useEffect, useRef } from "react";
import useElementClientBoundingRect from "../../../lib/useElementClientBoundingRect";
import "./style.scss";

interface Props {
  onChange: (newValue: number) => void;
  type?: "primary" | "white";
  value: number;
  start: number;
  end: number;
}

const Slider: FunctionComponent<Props> = ({ onChange, end, start, type = "primary", value }) => {
  const handleElementRef = useRef<HTMLDivElement | null>(null);
  const sliderElementRef = useRef<HTMLDivElement | null>(null);

  const handleClientRect = useElementClientBoundingRect(handleElementRef);
  const sliderClientRect = useElementClientBoundingRect(sliderElementRef);

  const draggingRef = useRef<boolean>(false);
  const onMouseTick = useRef<boolean>(false);

  const onMouse = useCallback(
    (event: MouseEvent) => {
      if (!handleElementRef || !sliderElementRef) {
        return;
      }
      event.preventDefault();

      if (event.type === "mousedown") {
        draggingRef.current = true;
        document.body.classList.add("sliderIsGrabbing");
      }

      if (!draggingRef.current) {
        return;
      }

      if (!onMouseTick.current || event.type === "mouseup") {
        const { pageX, type: eventType } = event;
        const sliderRect =
          sliderElementRef.current && sliderElementRef.current.getBoundingClientRect();
        const handleRect =
          handleElementRef.current && handleElementRef.current.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          if (!sliderRect || !handleRect) {
            return;
          }

          const positionX = pageX - sliderRect.left - handleRect.width / 2;
          const courseX = sliderRect.width - handleRect.width;
          const relativeX = clamp(positionX / courseX, 0, 1);
          const newValue = clamp(
            round(start + (end - start) * relativeX, 1),
            start <= end ? start : end,
            start <= end ? end : start
          );

          onChange(newValue);

          if (eventType === "mouseup") {
            draggingRef.current = false;
            document.body.classList.remove("sliderIsGrabbing");
          }

          onMouseTick.current = false;
        });

        onMouseTick.current = true;
      }
    },
    [onChange, start, end, handleElementRef, sliderElementRef]
  );

  useEffect(() => {
    const sliderElement = sliderElementRef.current;
    if (!sliderElement) {
      return;
    }

    sliderElement.addEventListener("mousedown", onMouse);
    document.addEventListener("mousemove", onMouse);
    document.addEventListener("mouseup", onMouse);

    return () => {
      sliderElement.removeEventListener("mousedown", onMouse);
      document.removeEventListener("mousemove", onMouse);
      document.removeEventListener("mouseup", onMouse);
    };
  }, [onMouse]);

  const style: CSSProperties = {};
  if (sliderClientRect && handleClientRect) {
    style.left =
      ((sliderClientRect.width - handleClientRect.width) * (value - start)) / (end - start);
  }

  return (
    <div
      className={classNames("Slider", {
        "Slider--primary": type === "primary",
        "Slider--white": type === "white",
      })}
      ref={sliderElementRef}
    >
      <div className="Slider-bar" />
      <div className="Slider-handle" ref={handleElementRef} style={style} />
    </div>
  );
};

export default Slider;
